import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faEye,
  faPenToSquare,
} from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import type { Artifact } from "@/services/api/agentic-v2";
import { artifactReportViewerUrl } from "@/components/artifacts/artifactReportViewer";
import { useOpenWindow } from "@/components/ui/os-layout";

const PLANNR_APP_URL = "https://app.plannrcrm.com";

export interface ArtifactActionButtonsProps {
  artifact: Artifact;
  compact?: boolean;
  className?: string;
}

/** View / Edit / Plannr actions — shared by Artefacts tab and Activity feed. */
export function ArtifactActionButtons({
  artifact,
  compact = false,
  className,
}: ArtifactActionButtonsProps) {
  const openWindow = useOpenWindow();

  const plannrUrl = artifact.storage?.plannr
    ? `${PLANNR_APP_URL}/contacts/${artifact.storage.plannr.account_uuid}`
    : null;

  const reportBuilder = artifact.storage?.report_builder ?? null;
  const reportBuilderId = reportBuilder?.report_id ?? null;
  const viewerUrl = artifactReportViewerUrl(artifact);
  const builderUrl = reportBuilder?.builder_url ?? null;

  const businessKind = (artifact.metadata as { business_kind?: string } | undefined)
    ?.business_kind;
  const isReactFormLoa =
    artifact.kind === "react_form" && businessKind === "loa";
  const reactPreviewUrl = isReactFormLoa
    ? `/loa-preview/${artifact._id}`
    : null;
  const storedPdfUrl = isReactFormLoa
    ? artifact.storage?.azure?.blob_url ?? null
    : null;
  const effectiveViewerUrl = isReactFormLoa ? null : viewerUrl;

  const hasActions =
    Boolean(plannrUrl) ||
    Boolean(effectiveViewerUrl) ||
    Boolean(reportBuilderId || builderUrl) ||
    Boolean(reactPreviewUrl) ||
    Boolean(isReactFormLoa) ||
    Boolean(storedPdfUrl);

  if (!hasActions) return null;

  const handleOpenReportBuilder = () => {
    if (!reportBuilderId) return;
    openWindow({
      appId: "report-builder",
      title: artifact.filename || "Report Builder",
      params: { report_id: reportBuilderId },
    });
  };

  const handleEditLoa = () => {
    openWindow({
      appId: "artifact",
      title: artifact.filename || "LOA",
      params: { id: artifact._id, mode: "edit" },
    });
  };

  return (
    <div className={clsx("flex flex-wrap items-center gap-1.5", className)}>
      {isReactFormLoa ? (
        <ActionButton
          icon={faPenToSquare}
          label="Edit"
          onClick={handleEditLoa}
          tone="primary"
          compact={compact}
          title="Edit LOA fields and re-render"
        />
      ) : null}
      {effectiveViewerUrl ? (
        <ActionButton
          icon={faEye}
          label="View"
          href={effectiveViewerUrl}
          tone="primary"
          compact={compact}
          title="Open rendered viewer (holdings + charts populate here)"
        />
      ) : null}
      {storedPdfUrl ? (
        <ActionButton
          icon={faEye}
          label="PDF"
          href={storedPdfUrl}
          tone="secondary"
          compact={compact}
          title="Open stored PDF"
        />
      ) : null}
      {plannrUrl ? (
        <ActionButton
          icon={faArrowUpRightFromSquare}
          label="Plannr"
          href={plannrUrl}
          tone="secondary"
          compact={compact}
          title="Open in Plannr"
        />
      ) : null}
      {reportBuilderId ? (
        <ActionButton
          icon={faPenToSquare}
          label="Edit"
          onClick={handleOpenReportBuilder}
          tone="secondary"
          compact={compact}
          title="Open in Report Builder"
        />
      ) : builderUrl ? (
        <ActionButton
          icon={faPenToSquare}
          label="Edit"
          href={builderUrl}
          tone="secondary"
          compact={compact}
          title="Open in Report Builder"
        />
      ) : null}
      {reactPreviewUrl ? (
        <ActionButton
          icon={faArrowUpRightFromSquare}
          label="Preview"
          href={reactPreviewUrl}
          tone="secondary"
          compact={compact}
          title="Open rendered preview"
        />
      ) : null}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  href,
  disabled,
  tone,
  compact = false,
  title,
}: {
  icon: typeof faEye;
  label: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  tone: "primary" | "secondary";
  compact?: boolean;
  title?: string;
}) {
  const base = clsx(
    "inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 shrink-0",
    compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs",
  );
  const toneCls =
    tone === "primary"
      ? "bg-indigo-500/90 text-white hover:bg-indigo-400"
      : "border border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-900 dark:border-transparent dark:bg-slate-800/60 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-200";

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        title={title ?? label}
        className={clsx(base, toneCls)}
        onClick={(e) => e.stopPropagation()}
      >
        <FontAwesomeIcon icon={icon} className="text-[10px]" />
        {label}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={title ?? label}
      className={clsx(base, toneCls)}
    >
      <FontAwesomeIcon icon={icon} className="text-[10px]" />
      {label}
    </button>
  );
}
